import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Logo } from "../components/Logo";
import { ThemeToggle } from "../components/ThemeToggle";
import { AmbientBlobs } from "../components/motion/AmbientBlobs";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, ArrowRight } from "lucide-react";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      const msg = error.response?.data?.detail || "Login failed. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex">
      {/* Left — form */}
      <div className="flex-1 flex flex-col px-6 lg:px-16 xl:px-24 py-10">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-950 transition-colors w-fit"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <ThemeToggle />
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-sm w-full mx-auto">
          <Logo size={40} tagline animated linkTo={null} className="mb-10" />

          <h1 className="font-display font-black text-4xl tracking-[-0.04em] leading-tight">
            Welcome back.
          </h1>
          <p className="text-ink-500 mt-2 font-light">Sign in to keep your campaigns running.</p>

          <form onSubmit={handleSubmit} className="space-y-5 mt-10">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-semibold text-ink-500 hover:text-ink-950 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-950"
                  aria-label="toggle password visibility"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              size="lg"
              className="w-full"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : (<>Sign in <ArrowRight className="w-4 h-4" /></>)}
            </Button>
          </form>

          <p className="mt-8 text-sm text-ink-500">
            Don't have an account?{" "}
            <Link to="/register" className="text-ink-950 font-semibold hover:underline underline-offset-4">
              Create one
            </Link>
          </p>

          <div className="mt-6 flex items-center gap-4 text-xs text-ink-500">
            <Link to="/privacy" className="hover:text-ink-950">Privacy</Link>
            <span className="w-1 h-1 rounded-full bg-ink-300" />
            <Link to="/terms" className="hover:text-ink-950">Terms</Link>
          </div>
        </div>
      </div>

      {/* Right — playful ambient */}
      <div className="hidden lg:block lg:flex-1 relative bg-ink-950 overflow-hidden">
        <AmbientBlobs />
        <div className="absolute inset-0 flex items-center justify-center p-16">
          <div className="relative max-w-md">
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-citrus-500">
              Today on XEA
            </p>
            <h2 className="font-display font-black text-white text-4xl tracking-[-0.03em] leading-tight mt-3">
              "Cinematic zoom on rim watch, 5s, audio on."
            </h2>
            <p className="mt-4 text-white/60 font-light">
              That prompt just became a downloadable mp4. Welcome to XEA.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
